/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { MONTHS, formatDate } from '@/lib/dates';
import { ApiError } from '@/lib/queryClient';
import { todayIn } from '@/lib/ranges';

/**
 * Out of office, in words and in moments (`out-of-office/spec.md`, `OOO-D4`
 * to `OOO-D7`).
 *
 * Kept apart from the screen so the rules can be read, and tested, without
 * rendering it. A period starts and ends at an instant, to the minute, and
 * is shown on **Amida's clock**: New York's, the zone the server counts in,
 * whatever the browser's own zone. "Today" here is New York's today, so a
 * person in London at one in the morning is still on New York's yesterday.
 *
 * The words are the server's own (`backend/src/common/notify/when.py`), held
 * to the same examples by tests, so a notice and the screen say a time alike.
 * Nothing here reads the clock: every function that needs "now" is given it.
 */

/** The zone a period is shown in: Amida's clock. */
export const AMIDA_ZONE = 'America/New_York';

/** The longest period, in days on Amida's calendar (`OOO-D4`). */
export const MOST_DAYS = 90;

/** The shortest period, in minutes (`OOO-D4`). */
export const LEAST_MINUTES = 10;

/** How far behind the server a browser's "now" may be and still count as now. */
export const CLOCK_SKEW_MS = 60_000;

/** As long as the server keeps a note to the colleague, and no longer (`OOO-3`). */
export const NOTE_MOST = 280;

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;
const EN_DASH = '–';

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Today in Amida's calendar, as `YYYY-MM-DD`. */
export function amidaToday(now: Date = new Date()): string {
  return todayIn(now, AMIDA_ZONE);
}

/** A `YYYY-MM-DD` day as a UTC date, or null when it is not a real day. */
function toDay(iso: string): Date | null {
  const match = ISO.exec(iso);
  if (!match) return null;
  const day = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return day.toISOString().slice(0, 10) === iso ? day : null;
}

/** The day `days` after `iso` (before, when negative). */
export function addDays(iso: string, days: number): string {
  const day = toDay(iso);
  if (!day) return iso;
  day.setUTCDate(day.getUTCDate() + days);
  return day.toISOString().slice(0, 10);
}

/** An ISO datetime as a moment, or null when it is not one. */
export function toMoment(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const moment = new Date(iso);
  return Number.isNaN(moment.getTime()) ? null : moment;
}

const WALL = new Intl.DateTimeFormat('en-CA', {
  timeZone: AMIDA_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** A moment as New York's wall clock shows it. */
export interface Wall {
  /** `YYYY-MM-DD` */
  date: string;
  /** `HH:MM`, 24-hour */
  time: string;
  hour: number;
  minute: number;
  /** The wall clock read as if it were UTC: for differences in wall time. */
  asUtc: number;
}

export function wall(moment: Date): Wall {
  const parts = WALL.formatToParts(moment);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? '0');
  const [year, month, day] = [part('year'), part('month'), part('day')];
  const [hour, minute, second] = [part('hour') % 24, part('minute'), part('second')];
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${String(year)}-${pad(month)}-${pad(day)}`,
    time: `${pad(hour)}:${pad(minute)}`,
    hour,
    minute,
    asUtc: Date.UTC(year, month - 1, day, hour, minute, second),
  };
}

/** How far New York's wall clock is from UTC at a moment, in ms (negative: behind). */
function offsetAt(ms: number): number {
  const whole = Math.floor(ms / 1000) * 1000;
  return wall(new Date(whole)).asUtc - whole;
}

/**
 * The moment a New York wall clock shows `date` at `time` (`HH:MM`), or null
 * when either is not one. The offset is New York's on that day, so daylight
 * saving is counted rather than assumed.
 */
export function amidaMoment(date: string, time: string): Date | null {
  const day = toDay(date);
  const match = HH_MM.exec(time);
  if (!day || !match) return null;
  const guess =
    day.getTime() + Number(match[1]) * 3_600_000 + Number(match[2]) * MINUTE_MS;
  const first = offsetAt(guess);
  const second = offsetAt(guess - first);
  return new Date(guess - (first === second ? first : second));
}

/** Midnight on New York's clock: the end of one day, the start of the next. */
export function isMidnight(moment: Date): boolean {
  const at = wall(moment);
  return at.hour === 0 && at.minute === 0;
}

/** The last New York day a period covers: the day before, when it ends at midnight. */
export function lastDay(endsAt: Date): string {
  return isMidnight(endsAt)
    ? wall(new Date(endsAt.getTime() - 1)).date
    : wall(endsAt).date;
}

/** Midnight on Amida's clock at the end of `now`'s day: "rest of today". */
export function midnightTonight(now: Date): Date | null {
  return amidaMoment(addDays(wall(now).date, 1), '00:00');
}

/** `2:30 pm`, `9:00 am`, `12:00 pm`, or `midnight`, on Amida's clock. */
export function clock(moment: Date): string {
  const at = wall(moment);
  if (at.hour === 0 && at.minute === 0) return 'midnight';
  const hour = at.hour % 12 || 12;
  return `${String(hour)}:${String(at.minute).padStart(2, '0')} ${at.hour < 12 ? 'am' : 'pm'}`;
}

/**
 * A day as short as it can be said: `17 Oct`, with the year only when it is
 * not this year's.
 */
export function shortDay(iso: string, today: string): string {
  const day = toDay(iso.slice(0, 10));
  if (!day) return formatDate(iso, '');
  const words = `${String(day.getUTCDate())} ${MONTHS[day.getUTCMonth()] ?? ''}`;
  return iso.slice(0, 4) === today.slice(0, 4) ? words : `${words} ${iso.slice(0, 4)}`;
}

/** "6–17 Oct", "28 Oct – 3 Nov", or with years where they are not this year. */
export function shortSpan(first: string, last: string, today: string): string {
  if (first === last) return shortDay(first, today);
  const sameYear = first.slice(0, 4) === last.slice(0, 4);
  const sameMonth = sameYear && first.slice(0, 7) === last.slice(0, 7);
  const end = shortDay(last, today);
  if (sameMonth) return `${String(Number(first.slice(8, 10)))}${EN_DASH}${end}`;
  const start = sameYear ? shortDay(first, last) : shortDay(first, today);
  return `${start} ${EN_DASH} ${end}`;
}

/**
 * Until when, as the tag and every notice say it: `2:30 pm` when it ends
 * later today, `midnight` at the end of today, otherwise the last day it
 * covers, `17 Oct`. A period of whole days ends at midnight after its last
 * day, which is the day said. `et` adds " ET" to a time, where a reader
 * might take it for their own zone.
 */
export function untilWords(endsAt: Date, now: Date, { et = false } = {}): string {
  const today = wall(now).date;
  const time = (words: string) => (et ? `${words} ET` : words);
  if (isMidnight(endsAt)) {
    return lastDay(endsAt) === today
      ? time('midnight')
      : shortDay(lastDay(endsAt), today);
  }
  if (wall(endsAt).date === today) return time(clock(endsAt));
  return shortDay(lastDay(endsAt), today);
}

/**
 * A period in words. Whole days as days (`6–17 Oct`); within a day as times
 * (`2:00 pm – 2:30 pm ET`); otherwise both ends with their days
 * (`6 Oct 2:00 pm – 8 Oct 9:00 am ET`) -- the server's own rule.
 *
 * `dated` names the day of a period within one day when that day is not
 * today, which the server's words leave out; `et: false` drops the "ET" for
 * a tag short enough to be read as Amida's.
 */
export function spanWords(
  startsAt: Date,
  endsAt: Date,
  now: Date,
  { et = true, dated = false } = {},
): string {
  const today = wall(now).date;
  const first = wall(startsAt);
  const last = wall(endsAt);
  const zone = et ? ' ET' : '';
  if (isMidnight(startsAt) && isMidnight(endsAt)) {
    return shortSpan(first.date, lastDay(endsAt), today);
  }
  if (
    first.date === last.date ||
    (isMidnight(endsAt) && lastDay(endsAt) === first.date)
  ) {
    const day = dated && first.date !== today ? `${shortDay(first.date, today)} ` : '';
    return `${day}${clock(startsAt)} ${EN_DASH} ${clock(endsAt)}${zone}`;
  }
  return `${shortDay(first.date, today)} ${clock(startsAt)} ${EN_DASH} ${shortDay(last.date, today)} ${clock(endsAt)}${zone}`;
}

/** When a period starts, after "Starts" or "will move": `on 6 Oct`, `at 2:00 pm ET`. */
export function startWords(startsAt: Date, now: Date): string {
  const today = wall(now).date;
  const at = wall(startsAt);
  if (isMidnight(startsAt)) return `on ${shortDay(at.date, today)}`;
  if (at.date === today) return `at ${clock(startsAt)} ET`;
  return `on ${shortDay(at.date, today)} at ${clock(startsAt)} ET`;
}

/** Whether a period is of whole days: from midnight to midnight on Amida's clock. */
export function isWholeDays(startsAt: Date, endsAt: Date): boolean {
  return isMidnight(startsAt) && isMidnight(endsAt);
}

/** Somebody's period as `/api/v1/people` gives it: when, never who covers. */
export interface AwayPeriod {
  away_from: string | null;
  away_until: string | null;
}

/**
 * The small tag beside a colleague's name in a picker, or null when they are
 * not away: "Out of office until 2:30 pm", "… until midnight", "… until
 * 17 Oct"; for a period still to come, "Out of office 20–24 Oct", or
 * "Out of office 2:00 pm – 2:30 pm" for one later today.
 */
export function awayWords(person: AwayPeriod, now: Date): string | null {
  const ends = toMoment(person.away_until);
  if (!ends) return null;
  const starts = toMoment(person.away_from);
  if (starts && starts.getTime() > now.getTime()) {
    return `Out of office ${spanWords(starts, ends, now, { et: false, dated: true })}`;
  }
  return `Out of office until ${untilWords(ends, now)}`;
}

/**
 * Whether somebody's period shares any moment with `startsAt`..`endsAt`.
 * One ending as the other starts does not: the server's rule (`OOO-6`).
 */
export function awayDuring(person: AwayPeriod, startsAt: Date, endsAt: Date): boolean {
  const until = toMoment(person.away_until);
  if (!until) return false;
  const from = toMoment(person.away_from);
  const fromMs = from ? from.getTime() : Number.NEGATIVE_INFINITY;
  return fromMs < endsAt.getTime() && startsAt.getTime() < until.getTime();
}

/** What is wrong with a period, as a code: the screen words each for its fields. */
export type PeriodProblemCode =
  | 'start_missing'
  | 'start_passed'
  | 'end_missing'
  | 'end_before_start'
  | 'end_passed'
  | 'too_short'
  | 'too_long';

/** The server's own words for each (`ooo.dates_invalid`, `ooo.too_short`, `ooo.too_long`). */
export const PROBLEM_WORDS: Record<PeriodProblemCode, string> = {
  start_missing: 'Choose when it starts.',
  start_passed: 'The start has passed. Choose now or a time after it.',
  end_missing: 'Choose when it ends.',
  end_before_start: 'The end comes before the start.',
  end_passed: 'The end has passed. Choose a time still to come.',
  too_short: `A period is at least ${String(LEAST_MINUTES)} minutes. Choose a later end.`,
  too_long: `A period can be at most ${String(MOST_DAYS)} days. Choose an earlier end.`,
};

/**
 * What is wrong with a period, or null: which end, and why.
 *
 * The server's rules (`OOO-2`, `OOO-D4`), in its order, checked here too so
 * the person hears about it beside the field rather than after Save. The
 * server decides again. `keepsStart` is for a running period, whose start is
 * the moment it began and so is already behind now.
 */
export function periodProblem(
  startsAt: Date | null,
  endsAt: Date | null,
  now: Date,
  keepsStart = false,
): { field: 'start' | 'end'; code: PeriodProblemCode } | null {
  if (!startsAt) return { field: 'start', code: 'start_missing' };
  if (!keepsStart && startsAt.getTime() < now.getTime() - CLOCK_SKEW_MS) {
    return { field: 'start', code: 'start_passed' };
  }
  if (!endsAt) return { field: 'end', code: 'end_missing' };
  if (endsAt.getTime() <= startsAt.getTime()) {
    return { field: 'end', code: 'end_before_start' };
  }
  if (endsAt.getTime() <= now.getTime()) return { field: 'end', code: 'end_passed' };
  if (endsAt.getTime() - startsAt.getTime() < LEAST_MINUTES * MINUTE_MS) {
    return { field: 'end', code: 'too_short' };
  }
  // Counted on Amida's calendar, so a period across the clocks going back is
  // not an hour too long.
  if (wall(endsAt).asUtc - wall(startsAt).asUtc > MOST_DAYS * DAY_MS) {
    return { field: 'end', code: 'too_long' };
  }
  return null;
}

function counted(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/**
 * The preview line under the form (spec §4): "2 approvals and 1 signature
 * will move to Priya Nair at 2:00 pm ET", or "on 6 Oct" for whole days.
 *
 * Counts only. When nothing waits on the person now, it says so, and what
 * happens to whatever arrives while they are away. For a period under a
 * day, signatures already sent stay with the person (`OOO-D6`), and it says
 * how many.
 */
export function previewWords(
  counts: { approvals: number; signatures: number; staying: number },
  colleague: string,
  startsAt: Date,
  now: Date,
): string {
  const parts = [
    counts.approvals > 0 ? counted(counts.approvals, 'approval', 'approvals') : null,
    counts.signatures > 0 ? counted(counts.signatures, 'signature', 'signatures') : null,
  ].filter((part): part is string => part !== null);
  const staying =
    counts.staying > 0
      ? ` ${counted(counts.staying, 'signature', 'signatures')} already sent ${
          counts.staying === 1 ? 'stays' : 'stay'
        } with you, because the absence is under a day.`
      : '';
  if (parts.length === 0) {
    return staying
      ? `Nothing will move to ${colleague} now.${staying} Anything that arrives while you are away goes to ${colleague}.`
      : `Nothing waits on you now. Anything that arrives while you are away goes to ${colleague}.`;
  }
  const when =
    startsAt.getTime() <= now.getTime() + CLOCK_SKEW_MS
      ? 'as soon as you save'
      : startWords(startsAt, now);
  return `${parts.join(' and ')} will move to ${colleague} ${when}.${staying}`;
}

/**
 * A signer out of office moved: *Covering for Dana Whitfield*, and once they
 * have signed, *Signed by Priya Nair, on behalf of Dana Whitfield* (`OOO-14`).
 */
export function coveringWords(signer: {
  name: string;
  status: string;
  covering_for: string | null;
}): string {
  if (!signer.covering_for) return '';
  return signer.status === 'signed'
    ? `Signed by ${signer.name}, on behalf of ${signer.covering_for}`
    : `Covering for ${signer.covering_for}`;
}

/** Where a moved item is now, in words. */
export function whereNow(
  outcome: 'with_colleague' | 'done' | 'returned' | 'could_not_move',
  colleague: string,
): string {
  switch (outcome) {
    case 'with_colleague':
      return `With ${colleague}`;
    case 'done':
      return `Done by ${colleague}`;
    case 'returned':
      return 'Back with you';
    case 'could_not_move':
      return 'Still with you';
  }
}

/** The problem codes that are about a period's times, not its colleague. */
const TIMES_CODES = new Set(['ooo.dates_invalid', 'ooo.too_short', 'ooo.too_long']);

/**
 * A refusal about the times, and which end it is about, so the server's
 * sentence shows beside that field; or null for any other refusal. The
 * server's start-has-passed sentence is the only one about the start.
 */
export function timesRefusal(
  error: unknown,
): { field: 'start' | 'end'; message: string } | null {
  if (!(error instanceof ApiError) || !TIMES_CODES.has(error.code) || !error.detail) {
    return null;
  }
  return {
    field: error.detail.startsWith('The start') ? 'start' : 'end',
    message: error.detail,
  };
}

/**
 * Why a change to the period did not go through. The server's `detail` is a
 * sentence written for the person -- the dates a colleague is away, or who to
 * talk to -- over codes and never values, so it is shown as it is (`SEC-4`).
 * Anything else gets our own words, never the raw error.
 */
export function refusalWords(error: unknown, doing = 'saved'): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return `Your session has ended, so that was not ${doing}. Sign in again.`;
    }
    if (error.detail) return error.detail;
  }
  return `That could not be ${doing}. Nothing has changed. Try again in a moment.`;
}
